/**
 * Audio fade utilities for smooth volume transitions
 */

/**
 * Apply a fade-in effect to an audio element
 *
 * @param audioElement - The HTML audio element
 * @param targetVolume - The final volume (0.0 to 1.0)
 * @param duration - Fade duration in seconds
 * @returns Promise that resolves when fade is complete
 */
export function applyFadeIn(
  audioElement: HTMLAudioElement,
  targetVolume: number,
  duration: number
): Promise<void> {
  return new Promise((resolve) => {
    if (duration <= 0) {
      audioElement.volume = targetVolume;
      resolve();
      return;
    }

    const startVolume = 0;
    const startTime = performance.now();
    const endTime = startTime + duration * 1000; // Convert to milliseconds

    audioElement.volume = startVolume;

    const fadeStep = () => {
      const currentTime = performance.now();
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);

      // Ease-in cubic curve for smooth start
      const easedProgress = progress * progress * progress;
      audioElement.volume = startVolume + (targetVolume - startVolume) * easedProgress;

      if (progress < 1) {
        requestAnimationFrame(fadeStep);
      } else {
        audioElement.volume = targetVolume;
        resolve();
      }
    };

    requestAnimationFrame(fadeStep);
  });
}

/**
 * Apply a fade-out effect to an audio element
 *
 * @param audioElement - The HTML audio element
 * @param duration - Fade duration in seconds
 * @returns Promise that resolves when fade is complete
 */
export function applyFadeOut(
  audioElement: HTMLAudioElement,
  duration: number
): Promise<void> {
  return new Promise((resolve) => {
    if (duration <= 0) {
      audioElement.volume = 0;
      resolve();
      return;
    }

    const startVolume = audioElement.volume;
    const startTime = performance.now();

    const fadeStep = () => {
      const currentTime = performance.now();
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);

      // Ease-out cubic curve for smooth end
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      audioElement.volume = startVolume * (1 - easedProgress);

      if (progress < 1) {
        requestAnimationFrame(fadeStep);
      } else {
        audioElement.volume = 0;
        resolve();
      }
    };

    requestAnimationFrame(fadeStep);
  });
}

/**
 * Calculate the current volume for a music track based on timeline position
 * Takes into account fade-in and fade-out regions
 *
 * @param offset - Current offset within the music track (seconds)
 * @param duration - Total duration of the music track (seconds)
 * @param fadeIn - Fade-in duration (seconds)
 * @param fadeOut - Fade-out duration (seconds)
 * @param baseVolume - Base volume level (0.0 to 1.0)
 * @returns The calculated volume (0.0 to 1.0)
 */
export function calculateFadeVolume(
  offset: number,
  duration: number,
  fadeIn: number = 0,
  fadeOut: number = 0,
  baseVolume: number = 1.0
): number {
  // Fade-in region
  if (offset < fadeIn) {
    const progress = offset / fadeIn;
    // Ease-in cubic curve
    const easedProgress = progress * progress * progress;
    return baseVolume * easedProgress;
  }

  // Fade-out region
  const fadeOutStart = duration - fadeOut;
  if (offset > fadeOutStart) {
    const progress = (offset - fadeOutStart) / fadeOut;
    // Ease-out cubic curve
    const easedProgress = 1 - Math.pow(1 - progress, 3);
    return baseVolume * (1 - easedProgress);
  }

  // Full volume region (between fade-in and fade-out)
  return baseVolume;
}
